import dynamic from 'next/dynamic';

import {
  useStickyElementBottom,
  useStickyFixed,
  useWindowWidth,
} from '@virginexperiencedays/hooks';
import { cn } from '@virginexperiencedays/tailwind';
import { screens } from '@virginexperiencedays/tailwind/screens';

import { ContentCard } from '@virginexperiencedays/components-v2/src/cards/ContentCard';
import { VirginLovesCard as BaseVirginLovesCard } from '@virginexperiencedays/components-v2/src/cards/VirginLoves';
import { Container } from '@virginexperiencedays/components-v2/src/layout/Container';
import { PurchasePanel } from '@virginexperiencedays/components-v2/src/pages/product/PurchasePanel';

import Stripe, { type StripeProps } from '../Stripe';
import { DataTestId } from '../VariationPage/PageUI';
import { AddToBasketWidget, type AddToBasketWidgetProps } from '../Widgets/AddToBasketWidget';
import { PriceWidget } from './PriceWidget';

import type { HTMLAttributes } from 'react';
import type { PageUIProps } from '../Page';

const Klarna = dynamic(() => import('../Klarna'), { ssr: false });

export type Props = Pick<PageUIProps, 'product'> &
  HTMLAttributes<HTMLElement> & {
    hidePurchasePanel?: boolean;
    addToBasket: AddToBasketWidgetProps;
    onStripeLoad?: StripeProps['onLoad'];
    onStripeClick?: StripeProps['onClick'];
    onStripeSuccess?: StripeProps['onSuccess'];
    onStripeError?: StripeProps['onError'];
  };

export const PurchasePanelWidget = ({
  product,
  hidePurchasePanel,
  addToBasket,
  onStripeLoad,
  onStripeClick,
  onStripeSuccess,
  onStripeError,
  className,
  ...rest
}: Props) => {
  const { width: windowWidth } = useWindowWidth();
  const { ref, isSticky } = useStickyElementBottom({
    initial: true,
    bottomOffset: 10, // required to avoid flashing on iOS
  });

  const isVirginLovesProduct = product?.facets?.features?.includes('virgin-loves');
  const showPayments = windowWidth >= screens.lg;

  if (hidePurchasePanel) {
    return (
      <PurchasePanelWidgetContainer
        isVirginLovesProduct={isVirginLovesProduct}
        className={className}
      />
    );
  }

  return (
    <PurchasePanelWidgetContainer isVirginLovesProduct={isVirginLovesProduct} className={className}>
      <PurchasePanel
        ref={ref}
        data-testid={DataTestId.PurchasePanel}
        data-dy={DataTestId.PurchasePanel}
        className={cn(
          'shadow-[0px_-4px_4px_0px_rgba(0,0,0,0.08)] lg:shadow-[0px_10px_14px_-5px_rgba(0,0,0,0.1),_0px_10px_10px_-5px_rgba(0,0,0,0.06)]',
          'dark:shadow-[0px_-4px_4px_0px_rgba(148,148,148,0.1)] dark:lg:shadow-[0px_10px_14px_-5px_rgba(148,148,148,0.1),_0px_10px_10px_-5px_rgba(148,148,148,0.1)]',
          '-mx-4 flex flex-col rounded-none border-0 md:-mx-10 lg:mx-0',
          !isSticky && 'shadow-none dark:shadow-none',
        )}
        {...rest}
      >
        {/* DY Promo */}
        <div
          className="hidden bg-[#FFEDEE] p-2"
          data-testid={DataTestId.PurchasePanelPromo}
          data-dy={DataTestId.PurchasePanelPromo}
        />
        <Container className="grid h-[var(--pp-h)] w-full grid-cols-[auto_1fr] items-center gap-4 p-4 lg:flex lg:h-auto lg:flex-col lg:items-start lg:gap-2 lg:px-6 lg:py-6 xl:px-6">
          {/* Add To Basket */}
          <PriceWidget
            {...product.price}
            className="items-start gap-0 md:gap-0 lg:items-center lg:gap-2"
          />
          <AddToBasketWidget {...addToBasket} />
          {/* Payment Providers */}
          <div className="hidden w-full flex-col lg:flex lg:gap-4">
            {showPayments && (
              <>
                <Stripe
                  productId={product?.id}
                  productSlug={product?.slug}
                  deliveryOptions={product?.deliveryOptions}
                  displayPrice={product?.price?.displayPrice}
                  className="empty:hidden"
                  onLoad={onStripeLoad}
                  onClick={onStripeClick}
                  onSuccess={onStripeSuccess}
                  onError={onStripeError}
                />
                <KlarnaCard displayPrice={product?.price?.displayPrice} />
              </>
            )}
          </div>
        </Container>
      </PurchasePanel>
    </PurchasePanelWidgetContainer>
  );
};

type ContainerProps = {
  isVirginLovesProduct?: boolean;
} & HTMLAttributes<HTMLDivElement>;

const PurchasePanelWidgetContainer = ({
  isVirginLovesProduct,
  className,
  children,
}: ContainerProps) => {
  const [ref] = useStickyFixed<HTMLDivElement>({ mobileOnly: true, placement: 'bottom', start: 'viewport' });

  return (
    <div
      ref={ref}
      data-testid={DataTestId.PurchasePanelWidgetContainer}
      className={cn(
        // px and negative mx so the element has proper width when fixed on mobile
        'flex flex-col gap-2 px-4 -mx-4 z-[12]',
        'lg:col-start-2 lg:row-start-2 lg:h-fit lg:w-[360px]',
        'lg:sticky lg:inset-x-0 lg:bottom-auto lg:top-4 lg:px-0 lg:mx-0',
        className,
      )}
    >
      {children}
      {isVirginLovesProduct && (
        <div
          className={cn(
            'relative hidden rounded-lg shadow-[0px_20px_24px_-5px_rgba(0,0,0,0.1),_0px_10px_10px_-5px_rgba(0,0,0,0.06)] lg:flex',
            // covers the shadow from purchase panel
            'after:absolute after:inset-x-[1px] after:top-[1px] after:z-[1] after:h-4 after:rounded-lg after:bg-background-page after:content-[""]',
          )}
        >
          <VirginLovesCard className="w-full border-border-neutral-faded py-4" />
        </div>
      )}
    </div>
  );
};

export const VirginLovesCard = ({ className }: { className?: string }) => (
  <ContentCard className={cn('px-4 py-2', className)} data-testid={DataTestId.VirginLoves}>
    <BaseVirginLovesCard iconSrc="/assets/icons/VirginLovesIcon.svg" />
  </ContentCard>
);

export const KlarnaCard = ({
  displayPrice,
  className,
  bordered,
}: {
  displayPrice: Props['product']['price']['displayPrice'];
  className?: string;
  bordered?: boolean;
}) => {
  if (displayPrice <= 35) return null;
  if (bordered) {
    return (
      <ContentCard className="p-3">
        <KlarnaCard className={className} displayPrice={displayPrice} />
      </ContentCard>
    );
  }

  return (
    <Klarna
      data-testid={DataTestId.PaymentProviders}
      className={className}
      price={displayPrice}
      compact
    />
  );
};
